// Clara LLM Bridge - Enhanced with Production Cache API
// Connects to Clara360 Production Cache Service on Port 5002

// Get user ID from auth context or session
const getUserId = () => {
  // Try to get user ID from various sources
  if (typeof window !== 'undefined') {
    // From localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        return user.id || user.user_id || 'default';
      } catch (e) {
        console.warn('Failed to parse user data from localStorage');
      }
    }
    
    // From sessionStorage
    const sessionUser = sessionStorage.getItem('user_id');
    if (sessionUser) {
      return sessionUser;
    }
    
    // From URL params (fallback)
    const urlParams = new URLSearchParams(window.location.search);
    const userId = urlParams.get('user_id');
    if (userId) {
      return userId;
    }
  }
  
  // Default fallback
  return 'default';
};

// Enhanced Clara LLM Query with Cache Support
export const queryClaraLLM = async (prompt, context = '') => {
  try {
    const userId = getUserId();
    console.log(`🧠 Querying Clara Cache API with prompt: "${prompt.substring(0, 50)}..." (User: ${userId})`);
    
    const startTime = Date.now();
    
    // Call Production Cache API on Port 5002
    const response = await fetch('/api/clara-llm', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        prompt: prompt,
        userId: userId,
        context: context
      })
    });

    if (!response.ok) {
      throw new Error(`Clara Cache API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    const processingTime = Date.now() - startTime;
    
    console.log(`✅ Clara Cache response received in ${processingTime}ms:`, {
      source: data.source,
      intent: data.intent,
      cache_used: data.cache_used,
      processing_time: data.processing_time,
      confidence: data.confidence
    });
    
    // Extract the content from the response
    if (data.response) {
      return {
        content: data.response.trim(),
        metadata: {
          source: data.source,
          intent: data.intent,
          cache_used: data.cache_used,
          processing_time: data.processing_time,
          confidence: data.confidence,
          user_id: data.user_id,
          model: data.model
        }
      };
    }
    
    throw new Error('No response in Clara Cache API response');
    
  } catch (error) {
    console.error('❌ Clara Cache Bridge error:', error);
    
    // Fallback to old API if cache fails
    console.log('🔄 Falling back to legacy Clara API...');
    try {
      const fallbackResponse = await fetch('/api/clara-llm', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt: prompt,
          context: context
        })
      });

      if (fallbackResponse.ok) {
        const fallbackData = await fallbackResponse.json();
        console.log('✅ Fallback API response received');
        
        if (fallbackData.response) {
          return {
            content: fallbackData.response.trim(),
            metadata: {
              source: 'legacy_fallback',
              intent: 'unknown',
              cache_used: false,
              processing_time: fallbackData.processing_time || 0,
              confidence: 0.7,
              user_id: getUserId(),
              model: 'legacy'
            }
          };
        }
      }
    } catch (fallbackError) {
      console.error('❌ Fallback API also failed:', fallbackError);
    }
    
    throw error;
  }
};

// Preload user cache (call on login)
export const preloadUserCache = async () => {
  try {
    const userId = getUserId();
    console.log(`🚀 Preloading cache for user ${userId}`);
    
    const response = await fetch('/api/preload', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId: userId
      })
    });

    if (!response.ok) {
      throw new Error(`Preload API error: ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ User cache preloaded successfully:', data);
    
    return data;
    
  } catch (error) {
    console.error('❌ Cache preload error:', error);
    // Don't throw - preload is optional
    return null;
  }
};

// Clear user cache (call on logout)
export const clearUserCache = async () => {
  try {
    const userId = getUserId();
    console.log(`🗑️ Clearing cache for user ${userId}`);
    
    const response = await fetch('/api/logout', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId: userId
      })
    });

    if (!response.ok) {
      throw new Error(`Logout API error: ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ User cache cleared successfully:', data);
    
    return data;
    
  } catch (error) {
    console.error('❌ Cache clear error:', error);
    // Don't throw - logout cleanup is optional
    return null;
  }
};

// Get cache status for user
export const getCacheStatus = async () => {
  try {
    const userId = getUserId();
    
    const response = await fetch(`/api/cache-status/${userId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`Cache status API error: ${response.status}`);
    }

    const data = await response.json();
    return data;
    
  } catch (error) {
    console.error('❌ Cache status error:', error);
    return { cached: false, error: error.message };
  }
};

// Get Top-5 queries directly
export const getTop5Queries = async () => {
  try {
    const userId = getUserId();
    
    const response = await fetch(`/api/top5/${userId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`Top5 API error: ${response.status}`);
    }

    const data = await response.json();
    return data.top5_queries;
    
  } catch (error) {
    console.error('❌ Top5 queries error:', error);
    return null;
  }
};

// Enhanced query with performance tracking
export const queryWithMetrics = async (prompt, context = '') => {
  const startTime = performance.now();
  
  try {
    const result = await queryClaraLLM(prompt, context);
    const totalTime = performance.now() - startTime;
    
    // Log performance metrics
    console.log(`📊 Query Performance:`, {
      prompt_length: prompt.length,
      total_time: Math.round(totalTime),
      api_time: result.metadata?.processing_time || 0,
      cache_used: result.metadata?.cache_used || false,
      source: result.metadata?.source || 'unknown'
    });
    
    return result;
    
  } catch (error) {
    const totalTime = performance.now() - startTime;
    console.error(`📊 Query Failed in ${Math.round(totalTime)}ms:`, error);
    throw error;
  }
};

// Backward compatibility - return just content for existing code
export const queryClaraLLMCompat = async (prompt, context = '') => {
  try {
    const result = await queryClaraLLM(prompt, context);
    return result.content;
  } catch (error) {
    throw error;
  }
};

export default { 
  queryClaraLLM, 
  queryClaraLLMCompat,
  preloadUserCache, 
  clearUserCache, 
  getCacheStatus, 
  getTop5Queries,
  queryWithMetrics
};

